import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, MapPin, Search, Navigation, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const purpleIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map event handler component
function MapEvents({ onMapClick, onReady }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;
    onReady(map);
    
    const handleClick = (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    };
    
    map.on('click', handleClick);
    
    // Critical: Fix map size for mobile
    setTimeout(() => map.invalidateSize(), 100);
    setTimeout(() => map.invalidateSize(), 500);
    
    return () => map.off('click', handleClick);
  }, [map, onMapClick, onReady]);
  
  return null;
}

export default function LocationPicker({ isOpen, onClose, onLocationSelect, initialLocation }) {
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [mapCenter, setMapCenter] = useState([51.5074, -0.1278]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const mapRef = useRef(null);
  const searchTimeout = useRef(null);

  // Get user location on open
  useEffect(() => {
    if (isOpen && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newCenter = [pos.coords.latitude, pos.coords.longitude];
          setMapCenter(newCenter);
          if (mapRef.current) {
            mapRef.current.setView(newCenter, 13);
          }
        },
        () => {},
        { timeout: 5000 }
      );
    }
  }, [isOpen]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      return data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch {
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  const handleMapClick = useCallback(async (lat, lng) => {
    const address = await reverseGeocode(lat, lng);
    setSelectedLocation({ lat, lng, address });
  }, []);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        setSearchResults(await res.json());
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleResultClick = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    setSelectedLocation({ lat, lng, address: result.display_name });
    setSearchResults([]);
    setSearchQuery(result.display_name.split(',')[0]);
    
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 15);
    }
  };

  const handleGetGPS = () => {
    if (!navigator.geolocation) return;
    
    setLoadingGPS(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const address = await reverseGeocode(lat, lng);
        
        setSelectedLocation({ lat, lng, address });
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 15);
        }
        setLoadingGPS(false);
      },
      () => {
        alert('Could not get location');
        setLoadingGPS(false);
      },
      { timeout: 10000 }
    );
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 p-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Select Location</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-gray-700">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search location..."
              className="pl-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400 animate-spin" />
            )}
            
            {searchResults.length > 0 && (
              <div className="absolute w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[10000]">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleResultClick(result)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-700 border-b border-gray-700 last:border-b-0"
                  >
                    <p className="text-sm text-white truncate">{result.display_name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={handleGetGPS}
            disabled={loadingGPS}
            className="border-gray-600 text-white hover:bg-gray-700"
          >
            {loadingGPS ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEvents onMapClick={handleMapClick} onReady={handleMapReady} />
          
          {selectedLocation && (
            <Marker 
              position={[selectedLocation.lat, selectedLocation.lng]} 
              icon={purpleIcon}
            />
          )}
        </MapContainer>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 p-4 border-t border-gray-700 flex-shrink-0">
        {selectedLocation ? (
          <div className="mb-3 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-200 line-clamp-2">{selectedLocation.address}</p>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-400 text-sm mb-3">
            Tap the map or search above to select a location
          </p>
        )}
        
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 border-gray-600 text-white hover:bg-gray-700" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className="flex-1 bg-purple-600 hover:bg-purple-700" 
            onClick={handleConfirm}
            disabled={!selectedLocation}
          >
            Confirm Location
          </Button>
        </div>
      </div>
    </div>
  );
}
